import { Alert, Button, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { closeModal } from 'actions/app'
import { getHarvestLogUrl } from 'api/field'
import { DONE, IN_PROGRESS } from 'common/c'
import SyncStatus from 'components/admin/manage-integrations/sync-status'
import ModalActions from 'components/modal/modal-actions'
import { TEST_IDS } from 'test-ids'

export const HARVEST_LOG_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
  ERROR: 'error',
} as const

type HarvestLogStatus =
  (typeof HARVEST_LOG_STATUS)[keyof typeof HARVEST_LOG_STATUS]

export type HarvestLogDownloadProps = {
  harvestId: number
}

// Like AwaitingLayerDownload, but the request is its own: a harvest without a
// log is reported here, with a close button, instead of in a separate alert.
export default function HarvestLogDownload({
  harvestId,
}: HarvestLogDownloadProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [status, setStatus] = useState<HarvestLogStatus>(
    HARVEST_LOG_STATUS.LOADING,
  )
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let current = true
    getHarvestLogUrl(harvestId)
      .then((logUrl) => {
        if (!current) return
        setUrl(logUrl)
        setStatus(
          logUrl ? HARVEST_LOG_STATUS.READY : HARVEST_LOG_STATUS.UNAVAILABLE,
        )
      })
      .catch(() => {
        if (current) setStatus(HARVEST_LOG_STATUS.ERROR)
      })
    return () => {
      current = false
    }
  }, [harvestId])

  const onClose = useCallback(() => {
    dispatch(closeModal())
  }, [dispatch])

  const onDownload = useCallback(() => {
    if (url) {
      window.location.href = url
      dispatch(closeModal())
    }
  }, [dispatch, url])

  const found =
    status === HARVEST_LOG_STATUS.LOADING || status === HARVEST_LOG_STATUS.READY

  return (
    <Stack data-testid={TEST_IDS.harvestLogDialog} spacing={3}>
      <Stack spacing={3} sx={{ alignItems: 'center' }}>
        {status === HARVEST_LOG_STATUS.LOADING && (
          <SyncStatus status={IN_PROGRESS} />
        )}
        {status === HARVEST_LOG_STATUS.READY && <SyncStatus status={DONE} />}
        {status === HARVEST_LOG_STATUS.UNAVAILABLE && (
          <Typography data-testid={TEST_IDS.harvestLogUnavailable}>
            {t('harvest_log_unavailable')}
          </Typography>
        )}
        {status === HARVEST_LOG_STATUS.ERROR && (
          <Alert severity="error" data-testid={TEST_IDS.harvestLogError}>
            {t('harvest_log_download_error')}
          </Alert>
        )}
      </Stack>
      <ModalActions>
        {found ? (
          <Button
            data-testid={TEST_IDS.harvestLogDownloadButton}
            variant="contained"
            onClick={onDownload}
            disabled={status !== HARVEST_LOG_STATUS.READY}
          >
            {t('download')}
          </Button>
        ) : (
          <Button
            data-testid={TEST_IDS.harvestLogCloseButton}
            variant="contained"
            onClick={onClose}
          >
            {t('close')}
          </Button>
        )}
      </ModalActions>
    </Stack>
  )
}
